// ============================================================
// Orymia Shared — Atmosphere & Conditions Types
// Orthogonal weather axes + environment medium + engine config
// ============================================================

/** Particle effect types (engine rendering) */
export const PARTICLE_TYPES = [
  "none",
  "dust",
  "fireflies",
  "snow",
  "rain",
  "stars",
] as const;
export type ParticleType = (typeof PARTICLE_TYPES)[number];

/** Time of day — drives lighting angle, sky color palette, atmosphere tone */
export const TIMES_OF_DAY = [
  "dawn",
  "morning",
  "noon",
  "afternoon",
  "dusk",
  "evening",
  "night",
] as const;
export type TimeOfDay = (typeof TIMES_OF_DAY)[number];
export const DEFAULT_TIME_OF_DAY: TimeOfDay = "afternoon";

// ─── Orthogonal Conditions Axes ────────────────────────────────────────────

/** What falls from above (or through) the scene. Medium-agnostic concept. */
export const PRECIPITATION_TYPES = [
  "none",
  "rain",
  "snow",
  "dust",
  "embers",
  "petals",
  "bubbles",
] as const;
export type PrecipitationType = (typeof PRECIPITATION_TYPES)[number];

/** How much of the sky/ceiling is covered. */
export const CLOUD_COVER_LEVELS = ["clear", "scattered", "overcast"] as const;
export type CloudCover = (typeof CLOUD_COVER_LEVELS)[number];

/** Wind conditions — first-class, not derived from weather. */
export interface WindConditions {
  /** Wind direction as 2D vector on XZ plane (normalized by compiler). Default: [1, 0] */
  direction: [number, number];
  /** Wind strength: 0 = calm, 1 = storm. Clamped to [0, 1] by compiler. Default: 0.1 */
  strength: number;
}

/** Visibility — explicit, not derived from precipitation. */
export const VISIBILITY_LEVELS = ["clear", "hazy", "dense"] as const;
export type Visibility = (typeof VISIBILITY_LEVELS)[number];

/** Composable atmospheric conditions — replaces the legacy WeatherState enum.
 *  Each axis is independent; partial objects are merged against DEFAULT_SPACE_CONDITIONS. */
export interface SpaceConditions {
  precipitation?: PrecipitationType;
  cloudCover?: CloudCover;
  wind?: WindConditions;
  visibility?: Visibility;
}

/** Centralized defaults — compiler merges partial conditions against this. */
export const DEFAULT_SPACE_CONDITIONS: Required<SpaceConditions> = {
  precipitation: "none",
  cloudCover: "clear",
  wind: { direction: [1, 0], strength: 0.1 },
  visibility: "clear",
};

// ─── Environment Medium ────────────────────────────────────────────────────

/** What fills the space. Determines how the compiler interprets conditions.
 *  Always explicit — never auto-inferred from terrain.
 *  Default: "air". Contradictory combinations produce compiler warnings. */
/** Environment medium constants — what fills the space (mirrors the LIGHT_TYPES pattern).
 *  Use these typed constants instead of raw string literals in compiler logic. */
export const SPACE_MEDIUM = {
  AIR: "air",
  UNDERWATER: "underwater",
  VOID: "void",
} as const;
export type SpaceMedium = (typeof SPACE_MEDIUM)[keyof typeof SPACE_MEDIUM];
/** Array form for validation/UI iteration. */
export const SPACE_MEDIUMS = Object.values(SPACE_MEDIUM);

// ─── Conditions Presets ────────────────────────────────────────────────────

/** Preset conditions for common weather scenarios.
 *  The ConditionsValue union type lets authors/LLM use shorthand strings.
 *  Note: "fog" uses precipitation: "none" — fog is purely a visibility effect. */
export const CONDITIONS_PRESETS = {
  clear: {
    precipitation: "none",
    cloudCover: "clear",
    wind: { direction: [1, 0], strength: 0.1 },
    visibility: "clear",
  },
  cloudy: {
    precipitation: "none",
    cloudCover: "overcast",
    wind: { direction: [1, 0.3], strength: 0.25 },
    visibility: "clear",
  },
  rain: {
    precipitation: "rain",
    cloudCover: "overcast",
    wind: { direction: [0.8, 0.6], strength: 0.5 },
    visibility: "hazy",
  },
  snow: {
    precipitation: "snow",
    cloudCover: "scattered",
    wind: { direction: [0.5, 0.5], strength: 0.3 },
    visibility: "hazy",
  },
  fog: {
    precipitation: "none",
    cloudCover: "clear",
    wind: { direction: [1, 0], strength: 0.05 },
    visibility: "dense",
  },
  storm: {
    precipitation: "rain",
    cloudCover: "overcast",
    wind: { direction: [0.7, 0.7], strength: 0.85 },
    visibility: "dense",
  },
  blizzard: {
    precipitation: "snow",
    cloudCover: "overcast",
    wind: { direction: [0.7, 0.7], strength: 0.8 },
    visibility: "dense",
  },
} as const satisfies Record<string, SpaceConditions>;

/** Type-safe shorthand key for preset conditions. */
export type ConditionsPresetKey = keyof typeof CONDITIONS_PRESETS;

/** Conditions value — either a full SpaceConditions object or a preset string shorthand. */
export type ConditionsValue = SpaceConditions | ConditionsPresetKey;

// ─── Engine Config (compiler output) ────────────────────────────────────────

/** Particle drift caused by wind — consumed by the client particle system.
 *  tiltAngle: how much precipitation tilts from vertical (0 = straight down). */
export interface ParticleDrift {
  tiltAngle: number;
  direction: [number, number];
}

/** Wind configuration for engine rendering (same shape as WindConditions). */
export interface WindConfig {
  /** Wind direction as a 2D unit vector on the XZ plane. Default: [1, 0] (east) */
  direction: [number, number];
  /** Wind strength: 0 = calm, 1 = storm. */
  strength: number;
}

/** Atmosphere/fog configuration — the engine language. */
export interface AtmosphereConfig {
  fogColor: string;
  fogNear: number;
  fogFar: number;
  particles?: ParticleType;
  /** Wind affecting all dynamic objects (vegetation sway, particle drift, fire lean). */
  wind?: WindConfig;
  /** Precipitation tilt caused by wind. Only present when precipitation + wind coexist. */
  particleDrift?: ParticleDrift;
}
